
import { ButtonType, ButtonWidget, CompactInlineAlertWidget, InlineAlertType, TabStyles, Tooltip, TooltipPosition } from '@tableau/widgets';
import * as clipboard from 'clipboard-polyfill';
import React from 'react';
import { match } from 'react-router-dom';

import { requestFailed } from '~src/app/actions/RequestFailedAction';
import { getAppStore } from '~src/app/AppStore';
import { BackButton } from '~src/app/layout/BackButton';
import { PageHeaderStyles } from '~src/app/layout/PageHeaderStyles';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { ActionsMenu } from '~src/app/spaces/ActionsMenu/ActionsMenu';
import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/common/react/CommonStyles';
import { Projects } from '~src/projects/resources/Projects';
import { cssClass } from '~src/style';
import { L10n } from '~src/ts/L10n/L10n';
import { IProjectInfo } from '~src/ts/models/IProjectInfo';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';

import { ConnectedApplicationInfoMapper } from '../models/ConnectedApplicationInfoMapper';
import { ConnectedApplications } from '../resources/ConnectedApplicationsResource';
import { CreateConnectedApplicationSecretResult } from '../serverApi/requests/CreateSecret';
import { callGetConnectedApplication } from '../serverApi/requests/GetConnectedApplication';
import { IConnectedApplicationSecretType, IConnectedApplicationType } from '../serverApi/types/ConnectedApplicationType';
import { SecretDisplayBox } from './SecretDisplayBox';

export type ConnectedApplicationDetailsSubspaceProps = { id: string };

// Weirdly just specifying the fact that our route type expects the ID to be defined is insufficient to actually have the ID in the type
// so we then need to specify that we do actually expect it in the "params" clause
export type ConnectedApplicationDetailsSubspaceRoutedProps = match<ConnectedApplicationDetailsSubspaceProps>;

export interface ConnectedApplicationDetailsSubspaceState {
  projectName?: string;
  connectedApplication: IConnectedApplicationType;
}

// we use cssClass to make inspection easier; see discussion
// https://tableau.slack.com/archives/C9H9ERB5G/p1567531204044200?thread_ts=1567122602.032800&cid=C9H9ERB5G
const cssPrefix = 'connected-application';

const backButtonSectionStyle = cssClass(cssPrefix, 'back-section', {
  display: 'flex',
  paddingLeft: 20,
  paddingTop: 15,
  alignItems: 'center',
});

export const connectedApplicationHeaderTestId = 'header';

const headerStyle = cssClass(cssPrefix, 'header', {
  fontSize: TabStyles.Typography.Sizing.XXLarge,
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey51,
  paddingLeft: 20,
  paddingTop: 5,
  display: 'flex',
  flexDirection: 'row',
});

export const connectedApplicationSubHeaderTestId = 'sub-header';

const subHeaderStyle = cssClass(cssPrefix, 'sub-header', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey51,
  paddingLeft: 20,
  paddingTop: 5,
});

const bodyStyle = cssClass(cssPrefix, 'body', {
  fontSize: TabStyles.Typography.Sizing.Default,
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey51,
  paddingLeft: 30,
  paddingTop: 10,
});

export const connectedApplicationSecretDetailsTestId = 'secret-details';
export const connectedApplicationClientIdDetailsTestId = 'client-id-details';
export const connectedApplicationAccessDetailsTestId = 'access-details';
export const connectedApplicationDomainsDetailsTestId = 'domains-details';


// rowStyle, labelStyle, detailsStyle, detailsBorderStyle, detailsStyleWithNoBorder, detailsStyleWithBorder, buttonCellStyleWithBorder, tableStyle
// all adapted from src\schedule\components\ScheduleDetailsSubspace.tsx
const tableStyle = cssClass(cssPrefix, 'table', {
  maxWidth: '768px',
  paddingRight: '18px',
  marginTop: '12px',
  maxHeight: '300px',
  display: 'block',
});

const rowStyle = {
  padding: '12px',
};

const labelStyle = cssClass(cssPrefix, 'label', {
  width: '1%',
  paddingLeft: '18px',
  textAlign: 'right',
  fontSize: '15px',
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey153,
  lineHeight: '23px',
  whiteSpace: 'nowrap',
}, rowStyle);

const detailsStyle = {
  width: 500,
  paddingTop: '15px',
  lineHeight: '1.35em',
};

const detailsBorderStyle = {
  borderBottom: `1px solid ${TabStyles.Colors.F7Disabled}`,
};

const detailsStyleWithBorder = cssClass(cssPrefix, 'details-border', rowStyle, detailsStyle, detailsBorderStyle, {
  whiteSpace: 'pre-line',
});

const secretsCell = cssClass(cssPrefix, 'details-secrets-cell', rowStyle, detailsStyle, detailsBorderStyle, {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 40, // Without this the details cell ends up smaller than the label and the divider ends up in the wrong place
});

const secretsCellLastRow = cssClass(cssPrefix, 'details-secrets-cell-last-row', detailsStyle, {
  display: 'flex',
  alignItems: 'end',
  flexDirection: 'row-reverse',
  justifyContent: 'space-between'
});

const cellDescriptionSubtextStyle = cssClass(cssPrefix, 'description-subtext', {
  fontSize: TabStyles.Typography.Sizing.Small,
  fontWeight: 'normal',
  color: CommonStyles.Colors.Grey178
});

const clientIdCell = cssClass(cssPrefix, 'details-client-cell', rowStyle, detailsStyle, detailsBorderStyle, {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

// Adapted from src\app\layout\BackButton.tsx
export function ConnectedApplicationBackButton() {
  return <div>
    <Tooltip getContent={() => <span>{Messages.back()}</span>} position={TooltipPosition.BottomRightCorner} testId={'back-button-tooltip'} >
      <ButtonWidget
        buttonType={ButtonType.IconOutline}
        testId="back"
        handleClick={ConnectedApplications.navigateToAllConnectedApplicationsPage}
        style={PageHeaderStyles.BackButton}
        getContent={state => {
          // Keep alt text blank since button has a title: TFS 1036309
          const useActiveIcon: boolean = state.hover || state.active || state.focus;
          const src = useActiveIcon ? BackButton.REGULAR_ICON_PATH_ACTIVE : BackButton.REGULAR_ICON_PATH;
          return <img width={24} height={24} src={src} style={{ border: '1px solid transparent' }} alt="" />;
        }}
      />
    </Tooltip>
  </div>;
}

export class ConnectedApplicationDetailsSubspace extends React.Component<ConnectedApplicationDetailsSubspaceRoutedProps, ConnectedApplicationDetailsSubspaceState>
{
  public constructor(props: ConnectedApplicationDetailsSubspaceRoutedProps) {
    super(props);
    this.state = { connectedApplication: null };
    this.handleClick = this.handleClick.bind(this);
    this.rerenderCallback = this.rerenderCallback.bind(this);
  }

  async componentDidMount() {
    const connectedApplicationId = this.props.params.id;
    if (!this.state.connectedApplication) {
      const result = await callGetConnectedApplication(connectedApplicationId);
      this.setState({ connectedApplication: result.connectedApplication });
    }

    try {
      const projectName = await this.getProjectName();
      this.setState({ projectName: projectName });
    } catch (error) {
      getAppStore().dispatch(requestFailed(error, Messages.errorUnknown()));
      this.setState({ projectName: Messages.connectedApplicationUnknownProject() });
    }
  }

  protected handleClick() {
    let currentSecrets: IConnectedApplicationSecretType[] = this.state.connectedApplication?.secret;
    if (currentSecrets === undefined) {
      currentSecrets = [];
    }
    ConnectedApplications.createSecret(this.props.params.id).then(
      (result: CreateConnectedApplicationSecretResult) => {
        currentSecrets.push(result.connectedApplicationSecret);
        let currentApp = this.state.connectedApplication;
        currentApp.secret = currentSecrets;
        this.setState({ connectedApplication: currentApp });
      });
  }

  public rerenderCallback(deletedSecretId: string) {
    let currentSecrets: IConnectedApplicationSecretType[] = this.state.connectedApplication?.secret;
    var index = currentSecrets.findIndex(x => x.id === deletedSecretId);
    if (index > -1) {
      currentSecrets.splice(index, 1);
    }
    let currentApp = this.state.connectedApplication;
    currentApp.secret = currentSecrets;
    this.setState({ connectedApplication: currentApp });
  }

  // Ideally we should just call Projects.getById(), but this API seems to take dbID instead of projectId
  // So we need to work with the relevant team to figure out how to pass in the correct id.
  // Currently, we just get all projects and find our project based on project luid match
  public async getProjectName(): Promise<string> {
    const projects: ResourceQuery<IProjectInfo> = Projects.all();
    let projectCount = await projects.getCount();
    let allProjects = (await projects.getSlice(0, projectCount)).items;
    let project = allProjects.find(p => p.luid === this.state.connectedApplication?.projectId);
    return project?.name;
  }

  public render(): JSX.Element {
    const urls = siteUrlsSelector(getAppStore().getState());
    return <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span className={backButtonSectionStyle}>
        <ConnectedApplicationBackButton />
        <a href={urls.settingsConnectedApplicationsUrl()} onClick={ConnectedApplications.navigateToAllConnectedApplicationsPage}>{Messages.connectedApplicationsBackText()}</a>
      </span>
      <br></br>
      <span className={headerStyle} data-tb-test-id={connectedApplicationHeaderTestId}>
        <div style={{ paddingRight: 5 }}>{this.state.connectedApplication?.name}</div>
        <ActionsMenu
          targetItems={this.state.connectedApplication ? ConnectedApplicationInfoMapper.map([this.state.connectedApplication]) : []}
          disabled={!this.state.connectedApplication}
          useIcon={true}
        />
      </span>
      <br></br>
      <span className={subHeaderStyle} data-tb-test-id={connectedApplicationSubHeaderTestId}>
        {Messages.connectedApplicationsStatus()}
        <strong style={{ paddingLeft: 5, paddingRight: 10 }}>{this.state.connectedApplication?.enabled ? Messages.connectedApplicationsEnabled() : Messages.connectedApplicationsDisabled()}</strong>
        {Messages.created()}
        <strong style={{ paddingLeft: 5 }}>{this.state.connectedApplication?.createdAt && L10n.localizedDate(this.state.connectedApplication.createdAt)}</strong>
      </span>
      <table className={tableStyle}>
        <tbody>
          <tr>
            <td className={labelStyle}>
              {Messages.connectedApplicationsDetailsSecret()}
              <br></br><span className={cellDescriptionSubtextStyle}>{Messages.connectedApplicationsMaximumOf2()}</span>
            </td>
            <td className={secretsCell} data-tb-test-id={connectedApplicationSecretDetailsTestId}>
              {this.state.connectedApplication?.secret?.length >= 1 &&
                <SecretDisplayBox
                  appId={this.props.params.id}
                  appName={this.state.connectedApplication.name}
                  secretId={this.state.connectedApplication.secret[0].id}
                  createdAt={this.state.connectedApplication.secret[0].createdAt}
                  rerenderCallback={this.rerenderCallback}
                />
              }
              {this.state.connectedApplication?.secret?.length === 2 &&
                <SecretDisplayBox
                  appId={this.props.params.id}
                  appName={this.state.connectedApplication.name}
                  secretId={this.state.connectedApplication.secret[1].id}
                  createdAt={this.state.connectedApplication.secret[1].createdAt}
                  rerenderCallback={this.rerenderCallback}
                />
              }
              <div className={secretsCellLastRow}>
                {(this.state.connectedApplication?.secret === undefined || this.state.connectedApplication.secret.length < 2) &&
                  <ButtonWidget buttonType={ButtonType.Primary} disabled={false} onClick={this.handleClick}>{Messages.connectedApplicationsDetailsGenerateNewSecret()}</ButtonWidget>
                }
                {(this.state.connectedApplication?.secret === undefined || this.state.connectedApplication.secret.length === 0) &&
                  <div><em>{Messages.connectedApplicationsDetailsNoActiveSecret()}</em></div>
                }
                {this.state.connectedApplication?.secret?.length === 2 &&
                  <><ButtonWidget buttonType={ButtonType.Primary} disabled={true}>{Messages.connectedApplicationsDetailsGenerateNewSecret()}</ButtonWidget>
                    <div style={{ paddingRight: 15 }}>
                      <CompactInlineAlertWidget
                        children={Messages.connectedApplicationsDeleteASecret()}
                        style={{ display: 'inline-flex' }}
                        type={InlineAlertType.Info}
                        testId='delete-a-secret-info' />
                    </div></>
                }
              </div>
            </td>
          </tr>
          <tr>
            <td className={labelStyle}>
              {Messages.connectedApplicationsClientId()}
            </td>
            <td className={clientIdCell} data-tb-test-id={connectedApplicationClientIdDetailsTestId}>
              {this.state.connectedApplication?.clientId}
              <ButtonWidget buttonType={ButtonType.Outline} onClick={async () => await clipboard.writeText(this.state.connectedApplication.clientId)}>
                {Messages.connectedApplicationsDetailsCopyClientId()}
              </ButtonWidget>
            </td>
          </tr>
          <tr>
            <td className={labelStyle}>
              {Messages.connectedApplicationsAccess()}
            </td>
            <td className={detailsStyleWithBorder} data-tb-test-id={connectedApplicationAccessDetailsTestId}>
              {this.state.projectName ? this.state.projectName : Messages.connectedApplicationsScopeAllProjects()}
            </td>
          </tr>
          <tr>
            <td className={labelStyle}>
              {Messages.connectedApplicationsDomainSafelist()}
            </td>
            <td className={detailsStyleWithBorder} data-tb-test-id={connectedApplicationDomainsDetailsTestId}>
              {(this.state.connectedApplication != null && this.state.connectedApplication.unrestrictedEmbedding != null && this.state.connectedApplication?.unrestrictedEmbedding === false) ? this.state.connectedApplication?.domainSafelist?.replace(/\s/g, '\n'): Messages.connectedApplicationsDomainsAll()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
  }

}

